import { isNull } from 'lodash'
import * as Yup from 'yup'
import { FORM_FIELDS } from './constants'

export const getInitialValues = (employee) => {
  if (!employee) {
    return {
      [FORM_FIELDS.FIRST_NAME]: '',
      [FORM_FIELDS.LAST_NAME]: '',
      [FORM_FIELDS.DISPLAY_NAME]: '',
      [FORM_FIELDS.EMAIL]: '',
      [FORM_FIELDS.PHONE]: '',
      [FORM_FIELDS.PHONE_FORMATTED]: '',
      [FORM_FIELDS.MOBILE_PHONE]: '',
      [FORM_FIELDS.STREET_ADDRESS]: '',
      [FORM_FIELDS.CITY]: '',
      [FORM_FIELDS.STATE]: '',
      [FORM_FIELDS.POSTAL_CODE]: '',
      [FORM_FIELDS.COUNTRY]: '',
      [FORM_FIELDS.USAGE_LOCATION]: '',
      [FORM_FIELDS.OTHER_MAILS]: [],
      [FORM_FIELDS.FAX]: '',
      [FORM_FIELDS.DEPARTMENT]: '',
      [FORM_FIELDS.JOB_TITLE]: '',
      [FORM_FIELDS.COMPANY_NAME]: '',
      [FORM_FIELDS.OFFICE_LOCATION]: '',
      [FORM_FIELDS.EMPLOYEE_ID]: '',
      [FORM_FIELDS.EMPLOYEE_TYPE]: '',
      [FORM_FIELDS.HIRE_DATE]: '',
      [FORM_FIELDS.MANAGER]: undefined,
      [FORM_FIELDS.M365_GROUPS]: [],
      [FORM_FIELDS.SECURITY_GROUPS]: [],
      [FORM_FIELDS.LICENSES]: [],
      [FORM_FIELDS.ENABLE_POWERSHELL_ACCESS]: false,
    }
  }

  // Manager ID can come from multiple sources - normalize to number
  let managerId
  if (employee.manager?.employeeId != null) {
    managerId = Number(employee.manager.employeeId)
  } else if (employee.managerId != null) {
    managerId = Number(employee.managerId)
  } else if (employee.manager_id != null) {
    managerId = Number(employee.manager_id)
  }
  managerId = managerId && !Number.isNaN(managerId) ? managerId : undefined

  return {
    [FORM_FIELDS.FIRST_NAME]: employee.first_name || '',
    [FORM_FIELDS.LAST_NAME]: employee.last_name || '',
    [FORM_FIELDS.DISPLAY_NAME]: employee.display_name || '',
    [FORM_FIELDS.EMAIL]: employee.email || '',
    [FORM_FIELDS.PHONE]: employee.phone || employee.businessPhone || '',
    [FORM_FIELDS.PHONE_FORMATTED]: employee.phone || employee.businessPhone || '',
    [FORM_FIELDS.MOBILE_PHONE]: employee.mobilePhone || '',
    [FORM_FIELDS.STREET_ADDRESS]: employee.streetAddress || '',
    [FORM_FIELDS.CITY]: employee.city || '',
    [FORM_FIELDS.STATE]: employee.state || '',
    [FORM_FIELDS.POSTAL_CODE]: employee.postalCode || '',
    [FORM_FIELDS.COUNTRY]: employee.country || '',
    [FORM_FIELDS.USAGE_LOCATION]: employee.usageLocation || '',
    [FORM_FIELDS.OTHER_MAILS]: employee.otherMails || [],
    [FORM_FIELDS.FAX]: employee.fax || '',
    [FORM_FIELDS.DEPARTMENT]: isNull(employee.department) ? '' : employee.department || '',
    [FORM_FIELDS.JOB_TITLE]: isNull(employee.job_title) ? '' : employee.job_title || '',
    [FORM_FIELDS.COMPANY_NAME]: employee.companyName || '',
    [FORM_FIELDS.OFFICE_LOCATION]: employee.officeLocation || '',
    [FORM_FIELDS.EMPLOYEE_ID]: employee.employeeId != null ? String(employee.employeeId) : '',
    [FORM_FIELDS.EMPLOYEE_TYPE]: employee.employeeType != null ? String(employee.employeeType) : '',
    [FORM_FIELDS.HIRE_DATE]: employee.hireDate ? new Date(employee.hireDate).toISOString().split('T')[0] : '',
    [FORM_FIELDS.MANAGER]: managerId || undefined,
    [FORM_FIELDS.M365_GROUPS]: employee.groupsByType?.M365Group?.map((g) => g.id) || [],
    [FORM_FIELDS.SECURITY_GROUPS]: employee.groupsByType?.SecurityGroup?.map((g) => g.id) || [],
    [FORM_FIELDS.LICENSES]:
      employee.m365Licenses?.filter((l) => l != null && l.licence_id).map((l) => l.licence_id) || [],
    [FORM_FIELDS.ENABLE_POWERSHELL_ACCESS]: employee.remotePowerShellEnabled || false,
  }
}

export const getValidationSchema = (intl) =>
  Yup.object({
    // Identity fields - Microsoft Graph API constraints
    [FORM_FIELDS.FIRST_NAME]: Yup.string()
      .min(1, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_SHORT' }))
      .max(64, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })) // Graph API: givenName max 64
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })),
    [FORM_FIELDS.LAST_NAME]: Yup.string()
      .min(1, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_SHORT' }))
      .max(64, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })) // Graph API: surname max 64
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })),
    [FORM_FIELDS.DISPLAY_NAME]: Yup.string().max(256, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: displayName max 256
    [FORM_FIELDS.EMAIL]: Yup.string()
      .email(intl.formatMessage({ id: 'AUTH.VALIDATION.INVALID_EMAIL' }))
      .min(5, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_SHORT' }))
      .max(256, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })) // Graph API: mail/userPrincipalName max 256
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })),

    // Contact fields - Microsoft Graph API constraints
    [FORM_FIELDS.PHONE]: Yup.string().max(64, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: businessPhones array items max 64
    [FORM_FIELDS.MOBILE_PHONE]: Yup.string().max(64, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: mobilePhone max 64
    [FORM_FIELDS.STREET_ADDRESS]: Yup.string().max(1024, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: streetAddress max 1024
    [FORM_FIELDS.CITY]: Yup.string().max(128, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: city max 128
    [FORM_FIELDS.STATE]: Yup.string().max(128, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: state max 128
    [FORM_FIELDS.POSTAL_CODE]: Yup.string().max(40, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: postalCode max 40
    [FORM_FIELDS.COUNTRY]: Yup.string().max(2, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: country ISO 3166-1 alpha-2 (2 chars)
    [FORM_FIELDS.USAGE_LOCATION]: Yup.string().max(2, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: usageLocation ISO 3166-1 alpha-2 (2 chars)
    [FORM_FIELDS.OTHER_MAILS]: Yup.array().of(
      Yup.string()
        .email(intl.formatMessage({ id: 'AUTH.VALIDATION.INVALID_EMAIL' }))
        .max(256, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: otherMails array items max 256
    ),
    [FORM_FIELDS.FAX]: Yup.string().max(64, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: faxNumber max 64

    // Job fields - Microsoft Graph API constraints
    [FORM_FIELDS.DEPARTMENT]: Yup.string().max(64, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: department max 64, optional
    [FORM_FIELDS.JOB_TITLE]: Yup.string().max(128, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: jobTitle max 128
    [FORM_FIELDS.COMPANY_NAME]: Yup.string().max(64, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: companyName max 64
    [FORM_FIELDS.OFFICE_LOCATION]: Yup.string().max(128, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: officeLocation max 128
    [FORM_FIELDS.EMPLOYEE_ID]: Yup.string().max(16, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: employeeId max 16
    [FORM_FIELDS.EMPLOYEE_TYPE]: Yup.string().max(16, intl.formatMessage({ id: 'AUTH.VALIDATION.TOO_LONG' })), // Graph API: employeeType max 16
    [FORM_FIELDS.HIRE_DATE]: Yup.string()
      .nullable()
      .matches(/^\d{4}-\d{2}-\d{2}$|^$/, intl.formatMessage({ id: 'AUTH.VALIDATION.INVALID_FIELD' })), // YYYY-MM-DD format or empty
    [FORM_FIELDS.MANAGER]: Yup.number().nullable(), // Manager ID (number or null)

    // Groups and licenses
    [FORM_FIELDS.M365_GROUPS]: Yup.array().of(Yup.string()),
    [FORM_FIELDS.SECURITY_GROUPS]: Yup.array().of(Yup.string()),
    [FORM_FIELDS.LICENSES]: Yup.array().of(Yup.string()),
    [FORM_FIELDS.ENABLE_POWERSHELL_ACCESS]: Yup.boolean(),

    // Formatted fields (not validated, derived from other fields)
    [FORM_FIELDS.PHONE_FORMATTED]: Yup.string(),
  })

/**
 * Transforms form values to API payload format
 * Separates SQL fields from Graph API fields
 * @param {Object} values - Form values
 * @param {Object} initialValues - Initial form values for comparison
 * @param {boolean} isIntegrated - Whether the employee has an integration (e.g. M365)
 * @param {{ isEmailEditable?: boolean }} options - When isEmailEditable is false (e.g. on-premises synced), mail is not sent to Graph
 * @returns {Object} API payload with sql and graphApiProperties
 */
export const toApiPayload = (values, initialValues = {}, isIntegrated = true, options = {}) => {
  const { isEmailEditable = true } = options
  const managerValue = values[FORM_FIELDS.MANAGER]
  const normalizedManagerId =
    managerValue &&
    (typeof managerValue === 'string' || typeof managerValue === 'number') &&
    String(managerValue).trim() !== ''
      ? managerValue
      : null

  const sqlPayload = {}

  if (values[FORM_FIELDS.FIRST_NAME] !== initialValues[FORM_FIELDS.FIRST_NAME]) {
    sqlPayload.first_name = values[FORM_FIELDS.FIRST_NAME]
  }
  if (values[FORM_FIELDS.LAST_NAME] !== initialValues[FORM_FIELDS.LAST_NAME]) {
    sqlPayload.last_name = values[FORM_FIELDS.LAST_NAME]
  }
  if (values[FORM_FIELDS.EMAIL] !== initialValues[FORM_FIELDS.EMAIL]) {
    sqlPayload.email = values[FORM_FIELDS.EMAIL]
  }
  if (values[FORM_FIELDS.DISPLAY_NAME] !== initialValues[FORM_FIELDS.DISPLAY_NAME]) {
    sqlPayload.display_name = values[FORM_FIELDS.DISPLAY_NAME] || null
  }
  if (values[FORM_FIELDS.PHONE] !== initialValues[FORM_FIELDS.PHONE]) {
    sqlPayload.phone = values[FORM_FIELDS.PHONE] || null
  }

  if (isIntegrated) {
    if (values[FORM_FIELDS.MOBILE_PHONE] !== initialValues[FORM_FIELDS.MOBILE_PHONE]) {
      sqlPayload.mobile_phone = values[FORM_FIELDS.MOBILE_PHONE] || null
    }
    if (values[FORM_FIELDS.DEPARTMENT] !== initialValues[FORM_FIELDS.DEPARTMENT]) {
      sqlPayload.department = values[FORM_FIELDS.DEPARTMENT] || ''
    }
    if (values[FORM_FIELDS.JOB_TITLE] !== initialValues[FORM_FIELDS.JOB_TITLE]) {
      sqlPayload.job_title = values[FORM_FIELDS.JOB_TITLE] || null
    }
    if (normalizedManagerId !== (initialValues[FORM_FIELDS.MANAGER] || null)) {
      sqlPayload.manager_id = normalizedManagerId
    }
  } else {
    // For non-integrated users, also include department and job_title
    if (values[FORM_FIELDS.DEPARTMENT] !== initialValues[FORM_FIELDS.DEPARTMENT]) {
      sqlPayload.department = values[FORM_FIELDS.DEPARTMENT] || ''
    }
    if (values[FORM_FIELDS.JOB_TITLE] !== initialValues[FORM_FIELDS.JOB_TITLE]) {
      sqlPayload.job_title = values[FORM_FIELDS.JOB_TITLE] || null
    }
  }

  const graphApiPayload = {}
  if (values[FORM_FIELDS.FIRST_NAME] !== initialValues[FORM_FIELDS.FIRST_NAME]) {
    graphApiPayload.givenName = values[FORM_FIELDS.FIRST_NAME]
  }
  if (values[FORM_FIELDS.LAST_NAME] !== initialValues[FORM_FIELDS.LAST_NAME]) {
    graphApiPayload.surname = values[FORM_FIELDS.LAST_NAME]
  }
  if (values[FORM_FIELDS.DISPLAY_NAME] !== initialValues[FORM_FIELDS.DISPLAY_NAME]) {
    graphApiPayload.displayName = values[FORM_FIELDS.DISPLAY_NAME] || null
  }
  // Keep Microsoft in sync:
  // - `userPrincipalName` is typically the Entra sign-in email.
  // - `mail` is the primary SMTP/contact email (also drives proxyAddresses).
  // Do not send when user is on-premises synced (isEmailEditable false).
  if (isEmailEditable && values[FORM_FIELDS.EMAIL] !== initialValues[FORM_FIELDS.EMAIL] && values[FORM_FIELDS.EMAIL]) {
    const trimmedEmail = values[FORM_FIELDS.EMAIL].trim()
    graphApiPayload.userPrincipalName = trimmedEmail
    graphApiPayload.mail = trimmedEmail
  }
  if (values[FORM_FIELDS.PHONE] !== initialValues[FORM_FIELDS.PHONE]) {
    graphApiPayload.businessPhones = values[FORM_FIELDS.PHONE] ? [values[FORM_FIELDS.PHONE]] : []
  }
  if (values[FORM_FIELDS.MOBILE_PHONE] !== initialValues[FORM_FIELDS.MOBILE_PHONE]) {
    graphApiPayload.mobilePhone = values[FORM_FIELDS.MOBILE_PHONE] || null
  }
  if (values[FORM_FIELDS.STREET_ADDRESS] !== initialValues[FORM_FIELDS.STREET_ADDRESS]) {
    graphApiPayload.streetAddress = values[FORM_FIELDS.STREET_ADDRESS] || null
  }
  if (values[FORM_FIELDS.CITY] !== initialValues[FORM_FIELDS.CITY]) {
    graphApiPayload.city = values[FORM_FIELDS.CITY] || null
  }
  if (values[FORM_FIELDS.STATE] !== initialValues[FORM_FIELDS.STATE]) {
    graphApiPayload.state = values[FORM_FIELDS.STATE] || null
  }
  if (values[FORM_FIELDS.POSTAL_CODE] !== initialValues[FORM_FIELDS.POSTAL_CODE]) {
    graphApiPayload.postalCode = values[FORM_FIELDS.POSTAL_CODE] || null
  }
  if (values[FORM_FIELDS.COUNTRY] !== initialValues[FORM_FIELDS.COUNTRY]) {
    graphApiPayload.country = values[FORM_FIELDS.COUNTRY] || null
  }
  if (values[FORM_FIELDS.USAGE_LOCATION] !== initialValues[FORM_FIELDS.USAGE_LOCATION]) {
    graphApiPayload.usageLocation = values[FORM_FIELDS.USAGE_LOCATION] || null
  }
  if (
    JSON.stringify(values[FORM_FIELDS.OTHER_MAILS] || []) !==
    JSON.stringify(initialValues[FORM_FIELDS.OTHER_MAILS] || [])
  ) {
    graphApiPayload.otherMails = values[FORM_FIELDS.OTHER_MAILS] || []
  }
  if (values[FORM_FIELDS.FAX] !== initialValues[FORM_FIELDS.FAX]) {
    graphApiPayload.faxNumber = values[FORM_FIELDS.FAX] || null
  }
  if (values[FORM_FIELDS.DEPARTMENT] !== initialValues[FORM_FIELDS.DEPARTMENT]) {
    graphApiPayload.department = values[FORM_FIELDS.DEPARTMENT] || ''
  }
  if (values[FORM_FIELDS.JOB_TITLE] !== initialValues[FORM_FIELDS.JOB_TITLE]) {
    graphApiPayload.jobTitle = values[FORM_FIELDS.JOB_TITLE] || null
  }
  if (values[FORM_FIELDS.COMPANY_NAME] !== initialValues[FORM_FIELDS.COMPANY_NAME]) {
    graphApiPayload.companyName = values[FORM_FIELDS.COMPANY_NAME] || null
  }
  if (values[FORM_FIELDS.OFFICE_LOCATION] !== initialValues[FORM_FIELDS.OFFICE_LOCATION]) {
    graphApiPayload.officeLocation = values[FORM_FIELDS.OFFICE_LOCATION] || null
  }
  if (values[FORM_FIELDS.EMPLOYEE_ID] !== initialValues[FORM_FIELDS.EMPLOYEE_ID]) {
    graphApiPayload.employeeId = values[FORM_FIELDS.EMPLOYEE_ID] || null
  }
  if (values[FORM_FIELDS.EMPLOYEE_TYPE] !== initialValues[FORM_FIELDS.EMPLOYEE_TYPE]) {
    graphApiPayload.employeeType = values[FORM_FIELDS.EMPLOYEE_TYPE] || null
  }
  if (values[FORM_FIELDS.HIRE_DATE] !== initialValues[FORM_FIELDS.HIRE_DATE]) {
    if (values[FORM_FIELDS.HIRE_DATE]) {
      const dateStr = values[FORM_FIELDS.HIRE_DATE]
      const [year, month, day] = dateStr.split('-').map(Number)
      const utcDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0))
      graphApiPayload.employeeHireDate = utcDate.toISOString()
    } else {
      graphApiPayload.employeeHireDate = null
    }
  }
  if (values[FORM_FIELDS.MANAGER] !== initialValues[FORM_FIELDS.MANAGER]) {
    graphApiPayload.managerId = normalizedManagerId
  }
  const m365GroupsChanged =
    JSON.stringify(values[FORM_FIELDS.M365_GROUPS] || []) !==
    JSON.stringify(initialValues[FORM_FIELDS.M365_GROUPS] || [])
  const securityGroupsChanged =
    JSON.stringify(values[FORM_FIELDS.SECURITY_GROUPS] || []) !==
    JSON.stringify(initialValues[FORM_FIELDS.SECURITY_GROUPS] || [])

  const groupsChanges = {}
  if (m365GroupsChanged) {
    const initialM365Groups = initialValues[FORM_FIELDS.M365_GROUPS] || []
    const newM365Groups = values[FORM_FIELDS.M365_GROUPS] || []
    groupsChanges.m365Groups = {
      toBeAdded: newM365Groups.filter((id) => !initialM365Groups.includes(id)),
      toBeRemoved: initialM365Groups.filter((id) => !newM365Groups.includes(id)),
    }
  }
  if (securityGroupsChanged) {
    const initialSecurityGroups = initialValues[FORM_FIELDS.SECURITY_GROUPS] || []
    const newSecurityGroups = values[FORM_FIELDS.SECURITY_GROUPS] || []
    groupsChanges.securityGroups = {
      toBeAdded: newSecurityGroups.filter((id) => !initialSecurityGroups.includes(id)),
      toBeRemoved: initialSecurityGroups.filter((id) => !newSecurityGroups.includes(id)),
    }
  }

  // Licenses changes (for API calls)
  const licensesChanged =
    JSON.stringify(values[FORM_FIELDS.LICENSES] || []) !== JSON.stringify(initialValues[FORM_FIELDS.LICENSES] || [])

  const licensesChanges = licensesChanged
    ? {
        toBeAdded: values[FORM_FIELDS.LICENSES] || [],
        toBeRemoved: initialValues[FORM_FIELDS.LICENSES] || [],
      }
    : undefined

  const powershellAccessChanged =
    values[FORM_FIELDS.ENABLE_POWERSHELL_ACCESS] !== initialValues[FORM_FIELDS.ENABLE_POWERSHELL_ACCESS]

  return {
    sql: sqlPayload,
    graphApiProperties: Object.keys(graphApiPayload).length > 0 ? graphApiPayload : undefined,
    groupsChanges: Object.keys(groupsChanges).length > 0 ? groupsChanges : undefined,
    licensesChanges,
    powershellAccessChanged: powershellAccessChanged ? values[FORM_FIELDS.ENABLE_POWERSHELL_ACCESS] : undefined,
  }
}
